// ./src/components/features/Highlights/HighlightEditDrawer/HighlightEditDrawer.tsx
//
// Create/edit drawer for a Highlight (Library entity). Self-loading in edit mode
// via recordId; context-or-props resolution; no success toast (the page highlight
// is the confirmation). Fields: name, group, icon (IconSelector), status.

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Stack, TextInput, Skeleton } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { Highlight, HighlightInput, HIGHLIGHT_GROUP_VALUES } from "@/utils/api";
import { getFormErrors, formatErrors, DRAWER_COMBOBOX_PROPS } from "@/utils";
import { useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { useOptionalHighlightsContext } from "@/contexts/HighlightsContext";
import { AppDrawer, StatusInput, IconSelector, SelectField } from "@/components/generic";

export interface HighlightEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  recordId?: string | null;
  /** Pre-fill the name when opening in add mode (e.g. from a "Create X" action). */
  initialName?: string | null;
  onCreated?: (highlight: Highlight) => void;
  onUpdated?: (highlight: Highlight) => void;
}

interface FormData {
  name: string;
  highlightGroup: string;
  icon: string | null;
  status: number;
}

const KNOWN_FORM_FIELDS = ["name", "highlightGroup", "icon", "status"];

const getInitialFormData = (): FormData => ({
  name: "",
  highlightGroup: "amenity",
  icon: null,
  status: STATUSES.ACTIVE,
});

const fromRecord = (record: Highlight): FormData => ({
  name: record.name || "",
  highlightGroup: record.highlightGroup || "amenity",
  icon: record.icon || null,
  status: record.status ?? STATUSES.ACTIVE,
});

const serialize = (data: FormData): string => JSON.stringify(data);

const HighlightEditDrawerSkeleton: React.FC = () => (
  <Stack gap="md">
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
  </Stack>
);

export const HighlightEditDrawer: React.FC<HighlightEditDrawerProps> = ({
  opened,
  onClose,
  recordId,
  initialName,
  onCreated,
  onUpdated,
}) => {
  const { t } = useTranslation(["highlights", "common"]);

  const ctx = useOptionalHighlightsContext();
  const loadFn = ctx?.get;
  const createFn = ctx?.create;
  const updateFn = ctx?.update;
  const creating = ctx?.isCreating ?? false;
  const updating = ctx?.isUpdating ?? false;
  const operationError = ctx?.lastError ?? null;
  const clearOperationError = ctx?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;
  const clearErrRef = useRef(clearOperationError);
  clearErrRef.current = clearOperationError;

  // ── Self-loading ──
  const [record, setRecord] = useState<Highlight | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);

  const showLoadingSkeleton = isLoadingRecord && !record;
  // An edit drawer WITHOUT its record (the read failed) shows the error only — its form still held the last record's
  // values, and Save wrote them over this one (#131 review). Save and ⌘S are off until the record is read.
  const withoutRecord = isEditMode && !record;

  useEffect(() => {
    let isCancelled = false;

    if (opened && isEditMode && recordId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      loadFnRef.current(recordId).then((result) => {
        if (isCancelled) {
          return;
        }

        if (result.didSucceed()) {
          setRecord(result.getDataFirst() as Highlight);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(result);
        }

        setIsLoadingRecord(false);
      });
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [opened, isEditMode, recordId]);

  // ── Form state ──
  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton || (isEditMode && !record)) {
      // Not dirty while closed, loading, or without its record (a failed read) — a stale flag from the last visit
      // asked "Discard changes?" over nothing (#131).
      setIsDirty(false);

      return;
    }

    setIsDirty(serialize(formData) !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton, isEditMode, record]);

  const effectiveError = loadError || operationError;

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(effectiveError, KNOWN_FORM_FIELDS),
    [effectiveError]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (isEditMode && !record) {
      return;
    }

    const newFormData = record
      ? fromRecord(record)
      : { ...getInitialFormData(), name: initialName?.trim() || "" };
    setFormData(newFormData);
    initialSnapshotRef.current = serialize(newFormData);
    setIsDirty(false);
    clearErrRef.current?.();
  }, [opened, record, isEditMode, initialName]);

  const setField = useCallback((field: keyof FormData, value: FormData[keyof FormData]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    clearErrRef.current?.();
    setLoadError(null);

    const submitData: HighlightInput = {
      name: formData.name.trim() || null,
      highlightGroup: formData.highlightGroup,
      icon: formData.icon,
      status: formData.status,
    };

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);
      const saved = result.data ? (result.data as Highlight[])[0] : null;

      if (saved) {
        if (isCreate) {
          onCreated?.(saved);
        } else {
          onUpdated?.(saved);
        }
      }

      onClose();
    }
  }, [formData, isEditMode, recordId, createFn, updateFn, onClose, onCreated, onUpdated]);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !isSubmitting && !showLoadingSkeleton && !withoutRecord,
    enabled: opened,
  });

  const drawerTitle = isEditMode
    ? t("highlights:form.editTitle", { defaultValue: "Edit highlight" })
    : t("highlights:form.addTitle", { defaultValue: "Add highlight" });

  const groupOptions = HIGHLIGHT_GROUP_VALUES.map((group) => ({
    value: group,
    label: t(`highlights:groups.${group}`, { defaultValue: group.replace(/_/g, " ") }),
  }));

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="md"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton || withoutRecord}
      isDirty={isDirty}
    >
      {withoutRecord && !showLoadingSkeleton ? null : showLoadingSkeleton ? (
        <HighlightEditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          <TextInput
            label={t("highlights:form.fields.name.label", { defaultValue: "Name" })}
            placeholder={t("highlights:form.fields.name.placeholder", { defaultValue: "e.g. Open Bar" })}
            value={formData.name}
            onChange={(e) => setField("name", e.currentTarget.value)}
            error={fieldErrors.name}
            data-autofocus
            size="md"
          />

          <SelectField
            label={t("highlights:form.fields.group.label", { defaultValue: "Group" })}
            data={groupOptions}
            value={formData.highlightGroup}
            onChange={(_name, v) => setField("highlightGroup", v || "amenity")}
            error={fieldErrors.highlightGroup}
            allowDeselect={false}
            comboboxProps={DRAWER_COMBOBOX_PROPS}
            size="md"
          />

          <IconSelector
            label={t("highlights:form.fields.icon.label", { defaultValue: "Icon" })}
            description={t("highlights:form.fields.icon.description", {
              defaultValue: "Shown next to the highlight on event pages.",
            })}
            value={formData.icon}
            onChange={(icon) => setField("icon", icon)}
            error={fieldErrors.icon}
            size="md"
          />

          <StatusInput
            value={formData.status}
            onChange={(status) => setField("status", status)}
            error={fieldErrors.status}
          />
        </Stack>
      )}
    </AppDrawer>
  );
};
